import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StudentPromotionFormModal from "../../components/Modals/StudentPromotionFormModal";
import { useCurrentStudent } from "../../hooks/useCurrentStudent";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useTdData } from "../../hooks/useTdData";
import { useMarkSheets } from "../../hooks/useMarkSheets";
import { getStudentData } from "../../utils/storage";

const StudentPromotion: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { studentData } = useCurrentStudent();
  const { selectedSession, selectedClass } = useSessionClass();
  const { getTcByStudentCode } = useTdData();
  const { useMarksheetByStudentId } = useMarkSheets(false, false);
  const [hasTcData, setHasTcData] = useState<boolean | null>(null);

  // Get studentCode from student data or storage
  const studentCode = studentData?.studentCode || getStudentData()?.studentCode;
  const studentId = studentData?.id ?? null;

  // Fetch marksheets for the student
  const { data: studentMarksheets } = useMarksheetByStudentId(
    studentId,
    !!studentId
  );

  // Fetch TC data for the individual student
  useEffect(() => {
    const fetchTcData = async () => {
      if (!studentCode || !selectedSession?.id || !selectedClass?.id) {
        setHasTcData(null);
        return;
      }

      try {
        const tcData = await getTcByStudentCode(
          selectedSession.id,
          selectedClass.id,
          studentCode
        );
        // If TC data is returned and not null, student has TC data
        setHasTcData(tcData !== null && tcData !== undefined);
      } catch (error: any) {
        // If 404 or "not found" error, student doesn't have TC data
        if (error?.response?.status === 404 || 
            error?.response?.data?.message?.toLowerCase().includes("not found") ||
            error?.response?.data?.message?.toLowerCase().includes("not created")) {
          setHasTcData(false);
        } else {
          // For other errors, assume no TC data to show the button
          setHasTcData(false);
        }
      }
    };

    fetchTcData();
  }, [studentCode, selectedSession?.id, selectedClass?.id, getTcByStudentCode]);

  // Check if student has PROMOTED status in marksheet
  const isPromoted = useMemo(() => {
    if (!studentMarksheets || !selectedClass?.id || !selectedSession?.id) {
      return false;
    }

    // Filter marksheets by current class and session
    const filteredMarksheets = Array.isArray(studentMarksheets) 
      ? studentMarksheets.filter((m: any) => 
          m.classId === selectedClass.id &&
          m.sessionId === selectedSession.id &&
          (m.marksheetType === "BOARD" || m.marksheetType === "ANNUAL")
        )
      : [];

    // Check if any marksheet has promote === "PROMOTED"
    return filteredMarksheets.some((m: any) => m.promote === "PROMOTED");
  }, [studentMarksheets, selectedClass?.id, selectedSession?.id]);

  // If student is already promoted, automatically navigate to dashboard
  useEffect(() => {
    if (isPromoted) {
      navigate("/student/dashboard");
    }
  }, [isPromoted, navigate]);

  // Don't show promotion option if TC data exists or if student is already promoted
  const shouldShowPromotion = hasTcData === false && !isPromoted;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold" }}
        >
          🎓 {t("promotion_form") || "Promotion Form"}
        </Typography>
        {shouldShowPromotion && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
            sx={{ textTransform: "none", fontWeight: "bold" }}
          >
            {t("fill_promotion_form") || "Fill Promotion Form"}
          </Button>
        )}
      </Paper>

      <StudentPromotionFormModal
        open={open}
        handleClose={() => setOpen(false)}
      />
    </Box>
  );
};

export default StudentPromotion;

